import { c as _c } from "react/compiler-runtime";
import React, { PropsWithChildren, useCallback, useState } from 'react';
import { Dialog, DialogContentWrapper, DialogTitle, DialogTrigger } from '@/comps/primitives/dialog';
import { WithLoadingContainer } from '@/comps/loading-spinner';
import { Input } from '@/comps/primitives/input';
import { Button } from '@/comps/primitives/button';
import { translateSafe } from '@/utils/translations';
import { useProjectsContext } from '@/providers/projects-provider';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { useAliasedKindName } from '@/contexts/alias';
import { buildFilterKindName } from '@/comps/FilterPicker/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/comps/primitives/select';
import { WithClassName, WithDisabled, WithOnChange } from '@/types/component';
import { OrganizationModel } from '@/models/organizations/internal-models';
import { useAuthContext } from '@/hooks/use-auth';
import { cn } from '@/utils/shad';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_ORGANIZATION_PROJECTS, QUERY_KEY_ORGANIZATIONS } from '@/constants/query-keys';
import { PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID, PARAMS_KEY_TIME_RANGE } from '@/constants/route-params-keys';
import { queryStateSerializer } from '@/utils/useQueryParamState/utils';
import { useAppNavigation } from '@/domains/navigation/use-app-navigation';
import { NavElement } from '@/domains/home/components/home-tabs/_components/nav-button';
import { FolderPlus } from 'lucide-react';
import type { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { filterToOperator, getOperatorLabel, isContainsFilter } from '@/comps/FilterPicker/filter-operators';
import { useCreateProject } from '@/api/generated/projects/projects';
type Props = PropsWithChildren & {
  onCancel?: () => void;
  onConfirm?: (projectId: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Unapplied draft to build the project from; falls back to the applied global filters. */
  filters?: GenericFilter[];
};
function getDisplayFilterValues(filter: GenericFilter): string[] {
  if (isContainsFilter(filter)) {
    const search = filter.search?.trim();
    return search ? [search] : [];
  }
  return filter.values;
}
export function CreateCustomProjectModal(t0) {
  const $ = _c(66);
  const {
    children,
    onConfirm,
    open: controlledOpen,
    onOpenChange,
    filters
  } = t0;
  const {
    filters: appliedGlobalFilters,
    timeRange,
    setKeepUrlTimerange,
    setFilters: setGlobalFilters
  } = useGlobalFiltersContext();
  const globalFilters = filters ?? appliedGlobalFilters;
  const {
    organizations,
    organizationId,
    selectedProject
  } = useProjectsContext();
  const [projectName, setProjectName] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  let t1;
  if ($[0] !== isControlled || $[1] !== onOpenChange) {
    t1 = nextOpen => {
      if (isControlled) {
        onOpenChange?.(nextOpen);
      } else {
        setInternalOpen(nextOpen);
      }
    };
    $[0] = isControlled;
    $[1] = onOpenChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setOpen = t1;
  const {
    isNebulyAdmin,
    isCompanyAdmin
  } = useAuthContext();
  const client = useQueryClient();
  const {
    navigateApp
  } = useAppNavigation();
  const aliasKindName = useAliasedKindName();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(organizationId);
  let t2;
  if ($[3] !== client || $[4] !== navigateApp || $[5] !== onConfirm || $[6] !== selectedOrganizationId || $[7] !== setGlobalFilters || $[8] !== setKeepUrlTimerange || $[9] !== setOpen || $[10] !== timeRange) {
    t2 = {
      mutation: {
        onSuccess: async t3 => {
          const {
            project
          } = t3;
          await client.invalidateQueries({
            queryKey: [QUERY_KEY_ORGANIZATIONS]
          });
          await client.invalidateQueries({
            queryKey: [QUERY_KEY_ORGANIZATION_PROJECTS]
          });
          setKeepUrlTimerange(true);
          setTimeout(() => {
            setGlobalFilters([]);
            navigateApp({
              route: "/reports",
              params: {
                [PARAMS_KEY_ORGANIZATION_ID]: queryStateSerializer(selectedOrganizationId, "string"),
                [PARAMS_KEY_PROJECT_ID]: queryStateSerializer(project.id, "string"),
                [PARAMS_KEY_TIME_RANGE]: queryStateSerializer(timeRange, "objectORnumberORboolean")
              }
            });
          }, 100);
          setOpen(false);
          onConfirm?.(project.id);
        }
      }
    };
    $[3] = client;
    $[4] = navigateApp;
    $[5] = onConfirm;
    $[6] = selectedOrganizationId;
    $[7] = setGlobalFilters;
    $[8] = setKeepUrlTimerange;
    $[9] = setOpen;
    $[10] = timeRange;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const {
    mutateAsync: createProject,
    isLoading: isCreating
  } = useCreateProject(t2);
  let t3;
  if ($[12] !== createProject || $[13] !== globalFilters || $[14] !== projectName || $[15] !== selectedOrganizationId || $[16] !== selectedProject?.id || $[17] !== selectedProject?.mappedProjectId) {
    t3 = () => {
      if (!selectedOrganizationId) {
        return;
      }
      if (selectedProject?.mappedProjectId) {
        return;
      }
      createProject({
        data: {
          name: projectName,
          organization_id: selectedOrganizationId,
          mapped_project_id: selectedProject?.id,
          filters: [...globalFilters]
        }
      });
    };
    $[12] = createProject;
    $[13] = globalFilters;
    $[14] = projectName;
    $[15] = selectedOrganizationId;
    $[16] = selectedProject?.id;
    $[17] = selectedProject?.mappedProjectId;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  selectedProject?.mappedProjectId;
  selectedProject?.id;
  const handleClick = t3;
  let t4;
  if ($[19] !== children) {
    t4 = children ? <DialogTrigger asChild={true}>{children}</DialogTrigger> : null;
    $[19] = children;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = translateSafe("common", "saveAsNewProject");
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const t6 = `${t5} (from: ${selectedProject?.name})`;
  let t7;
  if ($[22] !== t6) {
    t7 = <DialogTitle><div className="flex items-center justify-between border-b px-7"><NavElement Icon={FolderPlus} text={t6} coloredIcon={true} isActive={true} showTooltip={false} /></div></DialogTitle>;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-sm text-muted-foreground">Save the current project configuration as a{" "}<span className="underline underline-offset-2">new project</span>.</p>;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isCompanyAdmin || $[26] !== isNebulyAdmin || $[27] !== organizations || $[28] !== selectedOrganizationId) {
    t9 = (isNebulyAdmin || isCompanyAdmin) && <div className="flex flex-col gap-1.5"><label className="text-xs font-medium text-muted-foreground">{translateSafe("common", "organization")}</label><OrganizationsSelect value={selectedOrganizationId} options={organizations} onChange={setSelectedOrganizationId} /></div>;
    $[25] = isCompanyAdmin;
    $[26] = isNebulyAdmin;
    $[27] = organizations;
    $[28] = selectedOrganizationId;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== aliasKindName || $[31] !== globalFilters) {
    t10 = !!globalFilters.length && <div className="flex flex-col gap-1.5"><label className="text-xs font-medium text-muted-foreground">{translateSafe("common", "globalFilters")}</label><div className="flex flex-col divide-y rounded-md border">{globalFilters.map((filter, i) => {
          const displayValues = getDisplayFilterValues(filter);
          const operator = filterToOperator(filter);
          return <div key={i + filter.column} className="flex items-baseline gap-3 px-3 py-2 text-sm"><span className="w-32 shrink-0 text-xs text-muted-foreground">{buildFilterKindName(filter.column, aliasKindName)}</span><div className="flex flex-wrap gap-1"><span className="mr-0.5 rounded py-0.5 text-xs text-muted-foreground">{getOperatorLabel(operator)}</span>{displayValues.map(_temp)}</div></div>;
        })}</div></div>;
    $[30] = aliasKindName;
    $[31] = globalFilters;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <label htmlFor="project-name-input" className="text-xs font-medium text-muted-foreground">{translateSafe("common", "projectName")}</label>;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  let t13;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => setProjectName(e.currentTarget.value);
    t13 = translateSafe("common", "projectName");
    $[34] = t12;
    $[35] = t13;
  } else {
    t12 = $[34];
    t13 = $[35];
  }
  let t14;
  if ($[36] !== projectName) {
    t14 = <div className="flex flex-col gap-1.5">{t11}<Input id="project-name-input" value={projectName} onChange={t12} placeholder={t13} /></div>;
    $[36] = projectName;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t10 || $[39] !== t14 || $[40] !== t9) {
    t15 = <div className="flex flex-col gap-4 px-6 py-4">{t8}{t9}{t10}{t14}</div>;
    $[38] = t10;
    $[39] = t14;
    $[40] = t9;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== setOpen) {
    t16 = () => setOpen(false);
    $[42] = setOpen;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = translateSafe("common", "cancel");
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t16) {
    t18 = <Button onClick={t16} variant="outline">{t17}</Button>;
    $[45] = t16;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const t19 = !projectName;
  let t20;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = translateSafe("common", "confirm");
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== handleClick || $[49] !== t19) {
    t21 = <Button disabled={t19} onClick={handleClick} variant="default">{t20}</Button>;
    $[48] = handleClick;
    $[49] = t19;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t18 || $[52] !== t21) {
    t22 = <div className="flex items-center justify-end gap-2 border-t px-6 py-3">{t18}{t21}</div>;
    $[51] = t18;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== isCreating || $[55] !== t15 || $[56] !== t22) {
    t23 = <WithLoadingContainer loading={isCreating}>{t15}{t22}</WithLoadingContainer>;
    $[54] = isCreating;
    $[55] = t15;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t23 || $[59] !== t7) {
    t24 = <DialogContentWrapper className="max-h-[90vh] max-w-150 gap-0 overflow-y-auto p-0">{t7}{t23}</DialogContentWrapper>;
    $[58] = t23;
    $[59] = t7;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== open || $[62] !== setOpen || $[63] !== t24 || $[64] !== t4) {
    t25 = <Dialog open={open} onOpenChange={setOpen}>{t4}{t24}</Dialog>;
    $[61] = open;
    $[62] = setOpen;
    $[63] = t24;
    $[64] = t4;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  return t25;
}
function _temp(v, j) {
  return <span key={j + v} className={cn("rounded border px-1.5 py-0.5 text-xs")}>{v}</span>;
}
type OrganizationsSelectProps = WithDisabled & WithOnChange<string> & WithClassName & {
  value: string | undefined;
  options: OrganizationModel[];
};
const OrganizationsSelect = t0 => {
  const $ = _c(15);
  const {
    value,
    onChange,
    disabled,
    options,
    className
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange?.(e);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectValue placeholder="users" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== className || $[4] !== disabled) {
    t3 = <SelectTrigger className={className} disabled={disabled}>{t2}</SelectTrigger>;
    $[3] = className;
    $[4] = disabled;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options) {
    t4 = options.map(_temp2);
    $[6] = options;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <SelectContent>{t4}</SelectContent>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t5 || $[13] !== value) {
    t6 = <Select value={value} onValueChange={t1}>{t3}{t5}</Select>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t5;
    $[13] = value;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
function _temp2(org) {
  return <SelectItem key={org.id} value={org.id}><div className="flex items-center gap-1"><p>{org.name}</p></div></SelectItem>;
}